import { useEffect } from 'react'
import { useAuthStore } from '@/stores/authStore'
import { authApi } from '@/api/auth'

/**
 * Булев параметр подписки из authStore + принудительное обновление карты
 * параметров при монтировании (обёртка над тем же GET, что и
 * mergeSubscriptionParameters, но мерж — на актуального пользователя
 * в момент ответа, а не на снапшот).
 *
 * Зачем: карта subscription_parameters обновляется только ролевыми гвардами
 * (TeacherRoute/StudentRoute/AdminRoute). На страницах под общим ProtectedRoute
 * сессия, залогиненная до появления нового параметра, держит устаревшую карту
 * без ключа — и строгая проверка `=== 'true'` ошибочно показывает «нет доступа»
 * (симптом: «у учителя премиум, а модуль недоступен» до перелогина).
 *
 * enabled = false — обновление не выполняется (страницы, где параметр нужен
 * не всем ролям, не должны дергать API у каждого посетителя).
 */
export function useSubscriptionParameterFresh(key: string, enabled = true): boolean {
  const value = useAuthStore((s) => s.user?.subscription_parameters?.[key])
  const userId = useAuthStore((s) => s.user?.id ?? null)

  useEffect(() => {
    if (!enabled || userId === null) return
    let cancelled = false
    authApi
      .getSubscriptionParameters()
      .then((params) => {
        if (cancelled) return
        // Мержим на актуального пользователя в момент ответа, не на снапшот.
        const state = useAuthStore.getState()
        if (state.user) {
          state.setUser({ ...state.user, subscription_parameters: params })
        }
      })
      .catch(() => {
        // Ошибка обновления не критична: остаёмся на значении из стора.
      })
    return () => {
      cancelled = true
    }
  }, [enabled, userId])

  return value === 'true'
}
